// Direct upload to a presigned URL. Deliberately raw XHR: Ajax.put does a POST
// with an override header and attaches CSRF headers, both of which break the
// signature.
//
// The object lands in storage but no DB row exists yet, so the caller has to
// follow up with a create request.
//
// Callbacks are dispatched through the same `_*_callbacks` arrays an AjaxRequest
// exposes. Attachments#removeItem silences an aborted upload by emptying those
// arrays, which only works if they are the actual dispatch source — closures
// would keep firing and drive the upload counters negative.
const putDriver = {
  createsRecord: false,

  upload(descriptor, file, { progress, success, error, complete, timeout } = {}) {
    const xhr = new XMLHttpRequest();
    const request = {
      xhr,
      _success_callbacks: [],
      _error_callbacks: [],
      _complete_callbacks: [],
      _progress_callbacks: []
    };

    const register = (list, fn) => {
      if (typeof fn === 'function') request[list].push(fn);
    };
    register('_success_callbacks', success);
    register('_error_callbacks', error);
    register('_complete_callbacks', complete);
    register('_progress_callbacks', progress);

    // Reads the array at call time, so emptying it in between takes effect.
    const dispatch = (list, args) => {
      request[list].slice().forEach((fn) => fn.apply(null, args));
    };

    request.abort = function () {
      xhr.abort();
      dispatch('_error_callbacks', [xhr, 'aborted']);
    };

    xhr.open('PUT', descriptor.url, true);

    // Headers come straight from the descriptor: the server signed them, so
    // guessing a Content-Type here would invalidate the signature.
    const headers = descriptor.headers || {};
    if (!headers['Content-Type']) {
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    }
    Object.keys(headers).forEach((name) => {
      xhr.setRequestHeader(name, headers[name]);
    });

    xhr.upload.onprogress = function (e) {
      if (e.lengthComputable) dispatch('_progress_callbacks', [e.loaded / e.total]);
    };

    xhr.onreadystatechange = function () {
      if (xhr.readyState !== XMLHttpRequest.DONE) return;

      request._progress_callbacks = [];
      // status 0 means aborted, timed out or a network error; those paths
      // report themselves so the callbacks do not fire twice.
      if (xhr.status === 0) return;

      if (xhr.status < 400) {
        dispatch('_success_callbacks', [xhr.responseText, xhr]);
      } else {
        dispatch('_error_callbacks', [xhr, xhr.statusText]);
      }
      dispatch('_complete_callbacks', [xhr]);
    };

    xhr.onerror = function () {
      request._progress_callbacks = [];
      dispatch('_error_callbacks', [xhr, xhr.statusText]);
      dispatch('_complete_callbacks', [xhr]);
    };

    if (timeout) {
      xhr.timeout = timeout;
      xhr.ontimeout = function () {
        request._progress_callbacks = [];
        dispatch('_error_callbacks', [xhr, 'timeout']);
      };
    }

    xhr.send(file);
    return request;
  }
};

export default putDriver;
